import type { DriverResponse } from '@contracts/driver/DriverResponse';
import { DriversClient } from '@src/driver/services/DriversClient';
import type { DriverInformationInputsSchema } from '@src/orders/schemas/DriverInformationInputsSchema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type * as yup from 'yup';

interface Props {
  clientId: string;
  driverId: string;
  onSuccess: (updatedDriver: DriverResponse) => void;
}

export const useUpdateDriver = ({ clientId, driverId, onSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (data: yup.InferType<typeof DriverInformationInputsSchema>) =>
      DriversClient.update(clientId, driverId, {
        name: data.driverName!,
        email: data.driverEmail,
        phone: data.driverPhone,
        licenseNumber: data.driverLicenseNumber!,
      }),
    onSuccess: async (updatedDriver) => {
      onSuccess(updatedDriver);
      await queryClient.invalidateQueries({
        queryKey: ['drivers.list', clientId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['drivers.get', clientId, driverId],
      });
    },
  });

  return {
    updateDriver: mutate,
    isLoading: isPending,
  };
};
